import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const Process = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStatIndex, setCurrentStatIndex] = useState(0);
  const [statVisible, setStatVisible] = useState(true);
  const sectionRef = useRef(null);

  const stats = [
    {
      number: '30+',
      label: 'Clients',
      description: 'From USA, Australia, UAE and UK'
    },
    {
      number: '4',
      label: 'Countries',
      description: 'Global reach and presence'
    },
    {
      number: '100%',
      label: 'Trust',
      description: 'Symbol we proudly wear every day'
    }
  ];

  // Intersection Observer for scroll animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Auto-cycle through stats
  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setStatVisible(false);
        
        setTimeout(() => {
          setCurrentStatIndex((prev) => (prev + 1) % stats.length);
          setStatVisible(true);
        }, 300);
      }, 3000); // Change every 3 seconds

      return () => clearInterval(interval);
    }
  }, [isVisible, stats.length]);

  const currentStat = stats[currentStatIndex];

  return (
    <section ref={sectionRef} className="py-32 font-AfacadFluxLight relative overflow-hidden bg-slate-900">
      {/* Background gradient effects */}
      <div className="absolute -top-20 -left-20 w-[600px] h-[600px]">
        <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-gradient-to-br from-purple-500/15 via-indigo-500/10 to-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-20 left-20 w-[300px] h-[300px] bg-gradient-to-tl from-indigo-400/10 via-purple-400/8 to-violet-400/5 rounded-full blur-2xl"></div>
      </div>
      
      <div className="absolute -bottom-20 -right-20 w-[700px] h-[700px]">
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-indigo-500/20 via-purple-500/12 to-violet-500/8 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-[400px] h-[400px] bg-gradient-to-tl from-purple-400/15 via-indigo-400/10 to-blue-400/6 rounded-full blur-2xl"></div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-800 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="mb-6">
            <span className="text-sm font-semibold tracking-widest uppercase text-purple-300">
              About Us
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-white leading-tight mb-8">
            Your trusted startup<br />
            <span className="bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">
              assistance partner
            </span>
          </h2>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Content */}
          <div className={`lg:col-span-7 transition-all duration-1000 ease-out delay-200 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-indigo-400 rounded-full mt-3 flex-shrink-0"></div>
                <p className="text-lg text-gray-300 leading-relaxed">
                  <span className="text-white font-medium">VENTURECORP (Pvt) Ltd</span>, a portfolio company of the global partner <span className="text-white font-medium">VENTURECORP LK, LLC USA</span>, is Sri Lanka's premier start-up-assist organization for multinational start-ups.
                </p>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-2 h-2 bg-gradient-to-r from-indigo-400 to-blue-400 rounded-full mt-3 flex-shrink-0"></div>
                <p className="text-lg text-gray-300 leading-relaxed">
                  We take away your <span className="text-white font-medium">administrative headaches</span> from you so you can focus on your core business operations.
                </p>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full mt-3 flex-shrink-0"></div>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Our Sri Lankan entity serves over <span className="text-white font-medium">30 customers from USA, Australia, UAE and UK</span> which are based in Sri Lanka, which is a <span className="bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent font-medium">symbol of trust we proudly wear every day</span>.
                </p>
              </div>
            </div>

            {/* CTA Button */}
            <div className="mt-8">
              <Link to="/contact" className="inline-block bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 transform hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl text-center">
                Partner with us
              </Link>
            </div>
          </div>

          {/* Right Stats - Single Cycling Stat */}
          <div className={`lg:col-span-5 transition-all duration-1000 ease-out delay-400 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            <div className="text-center min-h-[200px] flex flex-col justify-center">
              <div className={`transition-all duration-300 ease-out ${
                statVisible ? 'opacity-100 transform translate-y-0 scale-100' : 'opacity-0 transform translate-y-4 scale-95'
              }`}>
                <div className="text-7xl md:text-8xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent mb-4">
                  {currentStat.number}
                </div>
                <h3 className="text-2xl md:text-3xl font-semibold text-white mb-4">
                  {currentStat.label}
                </h3>
                <p className="text-lg text-gray-400 leading-relaxed max-w-xs mx-auto">
                  {currentStat.description}
                </p>
              </div>
            </div>

            {/* Progress indicators */}
            <div className="flex justify-center mt-8 space-x-2">
              {stats.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentStatIndex 
                      ? 'bg-gradient-to-r from-purple-400 to-indigo-400 scale-125' 
                      : 'bg-gray-600'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process; 